<script setup lang="ts">
import { computed, ref, type StyleValue } from 'vue';
import {
    DEFAULT_INPUT_COUNTRY_CODE,
    type AddressInput,
    type AddressInputSource,
    type AddressingLocaleKey,
    useAddressInputWithValidation,
    useAddressFormatFields,
    getAddressFieldAutoCompleteValue,
    CountrySpan,
    AddressingCountryCode,
} from '@precon-tools-suite-by-shared/js-vue-commerceguys-addressing';

const props = withDefaults(
    defineProps<{
        name?: string | undefined;
        id?: string | undefined;
        modelValue?: AddressInputSource;
        scope?: boolean | string | number | symbol;
        defaultCountryCode?: string | undefined;
        loading?: boolean;
        allowPartial?: boolean;
        locale?: AddressingLocaleKey;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        textClass?: any;
        textStyle?: StyleValue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        countryCodeClass?: any;
        countryCodeStyle?: StyleValue;
        hideFlag?: boolean;
        countryCodeValueLabel?:
            | 'code'
            | 'name'
            | 'threeLetterCode'
            | 'currencyCode';
        countryCodeValueSize?: 'md' | 'lg';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        countryCodeValueClass?: any;
        countryCodeValueStyle?: StyleValue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        countryCodeValueFlagClass?: any;
        countryCodeValueFlagStyle?: StyleValue;
        countryCodeValueFlagSquare?: boolean;
        countryCodeValueFlagRounded?: boolean;
        countryCodeValueHideLabel?: boolean;
        countryCodeOptionLabel?:
            | 'code'
            | 'name'
            | 'threeLetterCode'
            | 'currencyCode';
        countryCodeOptionSize?: 'md' | 'lg';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        countryCodeOptionClass?: any;
        countryCodeOptionStyle?: StyleValue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        countryCodeOptionFlagClass?: any;
        countryCodeOptionFlagStyle?: StyleValue;
        countryCodeOptionFlagSquare?: boolean;
        countryCodeOptionFlagRounded?: boolean;
        countryCodeOptionHideLabel?: boolean;
    }>(),
    {
        name: undefined,
        id: undefined,
        modelValue: undefined,
        locale: undefined,
        scope: true,
        allowPartial: true,
        defaultCountryCode: DEFAULT_INPUT_COUNTRY_CODE,
        textClass: undefined,
        textStyle: undefined,
        countryCodeClass: undefined,
        countryCodeStyle: undefined,
        countryCodeValueLabel: 'name',
        countryCodeValueSize: undefined,
        countryCodeValueClass: undefined,
        countryCodeValueStyle: undefined,
        countryCodeOptionLabel: 'name',
        countryCodeOptionSize: undefined,
        countryCodeOptionClass: undefined,
        countryCodeOptionStyle: undefined,
        countryCodeValueFlagClass: undefined,
        countryCodeValueFlagStyle: undefined,
        countryCodeOptionFlagClass: undefined,
        countryCodeOptionFlagStyle: undefined,
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: AddressInput): void;
}>();

const {
    v,
    country,
    getFieldValue,
    getFieldErrors,
    updateFieldValue,
    fieldHasError,
    address,
} = useAddressInputWithValidation(() => props.modelValue, {
    $scope: props.scope,
    watchSource: true,
    onChanged: (newValue) => emit('update:modelValue', newValue),
    defaultCountryCode: props.defaultCountryCode,
    partial: () => props.allowPartial,
});

const { addressFormat, getFieldLabel } = useAddressFormatFields(
    () => country.value?.addressFormat
);

const fieldLines = computed(() => addressFormat.value.formatAddressInputKeys);

const name = computed(() => props.name ?? props.id);
const id = computed(() => props.id ?? props.name);

const fieldName = (field: string) =>
    name.value ? `${name.value}--${field}` : undefined;
const fieldId = (field: string) =>
    id.value ? `${id.value}--${field}` : undefined;

// ------------------------------------------------------------------------------------------------------------------ //
//  METHODS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

const countryDropdown = ref<{ show: () => void; hide: () => void } | null>(
    null
);
const showCountryDropdown = () => countryDropdown.value?.show();
const hideCountryDropdown = () => countryDropdown.value?.hide();
defineExpose({ countryDropdown, showCountryDropdown, hideCountryDropdown });
</script>

<template>
    <div
        :id="id"
        class="address-input"
        :class="{ 'address-input-loading': loading, 'p-invalid': v.$error }"
    >
        <div
            v-for="(line, index) in fieldLines"
            :key="index"
            class="address-input-line"
        >
            <template v-for="(field, hPos) in line">
                <slot
                    :id="fieldId(field)"
                    name="text"
                    :line="line"
                    :v-pos="index"
                    :h-pos="hPos"
                    :field-name="fieldName(field)"
                    :model-value="getFieldValue(field)"
                    :autocomplete="getAddressFieldAutoCompleteValue(field)"
                    :label="getFieldLabel(field)"
                    :placeholder="getFieldLabel(field)"
                    :has-error="fieldHasError(field)"
                    :errors="getFieldErrors(field)"
                    :address="address"
                    @update:model-value="(v) => updateFieldValue(field, v)"
                >
                    <InputText
                        :id="fieldId(field)"
                        :key="field"
                        class="address-input-field"
                        :class="{ 'p-invalid': fieldHasError(field) }"
                        :name="fieldName(field)"
                        :model-value="getFieldValue(field)"
                        :placeholder="getFieldLabel(field)"
                        :autocomplete="getAddressFieldAutoCompleteValue(field)"
                        @update:model-value="
                            (v) => updateFieldValue(field, v ?? null)
                        "
                    />
                </slot>
            </template>
        </div>

        <div class="address-input-line address-input-country">
            <slot
                :id="fieldId('countryCode')"
                name="countryCode"
                :class="countryCodeClass"
                :style="countryCodeStyle"
                :field-name="fieldName('countryCode')"
                :label="getFieldLabel('countryCode')"
                :placeholder="getFieldLabel('countryCode')"
                :address="address"
                :country="country"
                :country-code="address.countryCode"
                :options="Object.values(AddressingCountryCode)"
            >
                <Dropdown
                    :id="fieldId('countryCode')"
                    ref="countryDropdown"
                    class="address-input-field"
                    :class="countryCodeClass"
                    :style="countryCodeStyle"
                    :model-value="getFieldValue('countryCode')"
                    :name="fieldName('countryCode')"
                    :placeholder="getFieldLabel('countryCode')"
                    :virtual-scroller-options="{ itemSize: 32 }"
                    :filter-fields="['name', 'code']"
                    :options="Object.values(AddressingCountryCode)"
                    filter
                    auto-filter-focus
                    @update:model-value="
                        (v) => updateFieldValue('countryCode', v)
                    "
                >
                    <template #value="{ value, placeholder }">
                        <template v-if="value">
                            <CountrySpan
                                :key="value"
                                :code="value"
                                :hide-flag="hideFlag"
                                :label="countryCodeValueLabel"
                                :class="countryCodeValueClass"
                                :style="countryCodeValueStyle"
                                :flag-size="countryCodeValueSize"
                                :locale="locale"
                                :flag-class="countryCodeValueFlagClass"
                                :flag-style="countryCodeValueFlagStyle"
                                :flag-square="countryCodeValueFlagSquare"
                                :flag-rounded="countryCodeValueFlagRounded"
                                :hide-label="countryCodeValueHideLabel"
                            />
                        </template>
                        <template v-else>
                            <span class="text-400">
                                <template v-if="placeholder">{{
                                    placeholder
                                }}</template>
                                <template v-else>&nbsp;</template>
                            </span>
                        </template>
                    </template>
                    <template #option="{ option }">
                        <CountrySpan
                            :key="option"
                            :code="option"
                            :hide-flag="hideFlag"
                            :label="countryCodeOptionLabel"
                            :class="countryCodeOptionClass"
                            :style="countryCodeOptionStyle"
                            :flag-size="countryCodeOptionSize"
                            :locale="locale"
                            :flag-class="countryCodeOptionFlagClass"
                            :flag-style="countryCodeOptionFlagStyle"
                            :flag-square="countryCodeOptionFlagSquare"
                            :flag-rounded="countryCodeOptionFlagRounded"
                            :hide-label="countryCodeOptionHideLabel"
                        />
                    </template>
                </Dropdown>
            </slot>
        </div>
        <div class="address-input-overlay">
            <i class="pi pi-spinner pi-spin" />
        </div>
    </div>
</template>

<style scoped lang="scss">
.address-input {
    border-radius: var(--border-radius);
    background-color: var(--surface-0);
    border: 1px solid var(--surface-border);
    position: relative;

    &.p-invalid {
        border-color: var(--red-500);
    }

    &.address-input-loading {
        overflow: hidden;

        .address-input-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--maskbg);

            .pi {
                font-size: 3rem;
            }
        }
    }

    .address-input-overlay {
        display: none;
    }

    .address-input-line {
        display: flex;

        .address-input-field {
            border-radius: 0;
            border: none;
            border-bottom: 1px solid var(--surface-border);
            border-right: 1px solid var(--surface-border);
            width: 100%;
            background: none;

            &:last-child {
                border-right: none;
            }
        }

        &:first-child {
            .address-input-field:first-child {
                border-top-left-radius: var(--border-radius);
            }

            .address-input-field:last-child {
                border-top-right-radius: var(--border-radius);
            }
        }

        &.address-input-country {
            .p-dropdown {
                display: flex;
                flex-grow: 1;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
            }
            .address-input-field {
                border-bottom: none;
            }
            .address-input-field:first-child {
                border-bottom-left-radius: var(--border-radius);
            }

            .address-input-field:last-child {
                border-bottom-right-radius: var(--border-radius);
            }
        }
    }
}
</style>
